/**
 * Certificate of Vaccination — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner details, patient details.
 * Manual fields: vaccination names, batch numbers, dates given, next due, vet name.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatCalendarDate,
  formatSex,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import {
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface VaccinationCertificateFields {
  vaccinations: string
  batchNumbers: string
  datesGiven: string
  nextVaccineDue: string
  vetName: string
}

export const defaultVaccinationCertificateFields: VaccinationCertificateFields = {
  vaccinations: '',
  batchNumbers: '',
  datesGiven: '',
  nextVaccineDue: '',
  vetName: '',
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function VaccinationCertificatePdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: VaccinationCertificateFields
}) {
  const { patient, owner, clinic } = context
  // Generated output with no viewer to follow — resolve from the owning member's region.
  const fmt = resolveDateFormat(null, owner.region)
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>Certificate of Vaccination</Text>

        <OwnerDetails owner={owner} />

        {/* Patient details */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Patient Name: </Text>
            {patient.name}
          </Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Species; Breed: </Text>
            {patient.species}; {patient.breed || '—'}
          </Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Sex: </Text>
            {formatSex(patient.sex, patient.isNeutered)}
          </Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Date of Birth; Microchip number: </Text>
            {patient.dateOfBirth ? formatCalendarDate(patient.dateOfBirth, fmt) : '—'};{' '}
            {patient.microchipNumber || '—'}
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            This is a document to certify {patient.name}'s vaccination details.
          </Text>
        </View>

        {/* Vaccine details */}
        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold, textDecoration: 'underline' }}>
            Vaccine Certificate:
          </Text>
          <Spacer height={mm(2)} />
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.vaccinations || undefined} />}>
              <Text>• Vaccination(s):</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.batchNumbers || undefined} />}>
              <Text>• Batch number(s):</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.datesGiven || undefined} />}>
              <Text>• Date(s) given:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.nextVaccineDue || undefined} />}>
              <Text>• Next Vaccine(s) Due:</Text>
            </WithInlineResponse>
          </View>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            Please feel free to contact us for any further information or clarification. On behalf
            of Snoots Vet.
          </Text>
        </View>

        <VetSignoff vetName={fields.vetName} label="Dr" showSignature showDate />
      </Page>
    </Document>
  )
}
